import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export type ShopifyCartItem = {
  key: string;
  id: number;
  product_id: number;
  quantity: number;
  sku: string;
  title: string;
  price: number;
  final_line_price: number;
  properties?: Record<string, string> | null;
};

export type ShopifyCart = {
  token: string;
  item_count: number;
  total_price: number;
  items: ShopifyCartItem[];
};

export type ShopifyCartSections = Record<string, string>;

export type ShopifyCartWithSections = ShopifyCart & {
  sections?: ShopifyCartSections;
};

export type AddToCartInput = {
  id: number;
  quantity: number;
  selling_plan?: number;
  properties?: Record<string, string>;
};

export type AddItemsToCartInput = {
  items: AddToCartInput[];
  sections?: string[];
  sections_url?: string;
};

export type ChangeLineInput = {
  id: string | number;
  quantity: number;
};

export type UpdateCartInput = {
  updates: Record<string, number>;
};

export const shopifyCartApi = createApi({
  reducerPath: "shopifyCartApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["Cart"],
  endpoints: (builder) => ({
    getCart: builder.query<ShopifyCart, void>({
      query: () => "cart.js",
      providesTags: ["Cart"],
    }),
    addToCart: builder.mutation<ShopifyCart, AddToCartInput>({
      query: (body) => ({
        url: "cart/add.js",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Cart"],
    }),
    addItemsToCart: builder.mutation<
      ShopifyCartWithSections,
      AddItemsToCartInput
    >({
      query: (body) => ({
        url: "cart/add.js",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Cart"],
    }),
    changeLine: builder.mutation<ShopifyCart, ChangeLineInput>({
      query: (body) => ({
        url: "cart/change.js",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Cart"],
    }),
    updateCart: builder.mutation<ShopifyCart, UpdateCartInput>({
      query: (body) => ({
        url: "cart/update.js",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Cart"],
    }),
  }),
});

export const {
  useLazyGetCartQuery,
  useGetCartQuery,
  useAddToCartMutation,
  useAddItemsToCartMutation,
  useChangeLineMutation,
  useUpdateCartMutation,
} = shopifyCartApi;
